// H2.3 WO#3a §Context — Discriminated union describing the shell-facing
// "current context" state that the viewer PUSHES upward via
// `onContextStateChange`. This is the ONLY reading channel: no
// imperative `getContextState()` / subscribe surface exists. The shell
// renders this state verbatim — it MUST NOT recompute or duplicate the
// underlying business data (devil-review: zero shadow state).
//
// Authority boundary (frozen by WO#3a):
//   * The viewer emits ONLY truth it OWNS: cell taps in browse mode
//     (`selection`), a resolved parcel detail (`parcel`), or the neutral
//     baseline (`empty`).
//   * The viewer does NOT emit `purchase`. Purchase is a shell-level
//     concern composed from route (`#purchase`) + the committed
//     rectangle already flowing through the existing `onCommitSelection`
//     data channel. See `ui/grid-context.ts` for shell composition.
//
// This file is a PURE data module: no React, no imports from `ui/`.

import type { GridSelection } from "../lib/grid-selection";
import type { Parcel } from "./types";

/** Sub-state for a browse-mode empty-area tap. */
export type ViewerCellState =
  | { kind: "outside"; x: number; y: number }
  | { kind: "loading-map"; x: number; y: number }
  | { kind: "confirming"; x: number; y: number }
  | { kind: "empty"; x: number; y: number; asOf: number | null }
  | { kind: "confirm-error"; x: number; y: number };

/** Parcel detail payload attached to `parcel` context state. */
export interface ViewerParcelDetail {
  parcel: Parcel;
  asOfBlock: number | null;
  reconciled: boolean;
  fetchError?: string;
}

/**
 * Browse-mode "selection" (tap on empty area or overlapping parcels).
 * The viewer resolves cell / conflict beneath the pointer; the shell
 * renders whatever comes.
 */
export type ViewerSelectionState =
  | { kind: "cell"; cell: ViewerCellState }
  | { kind: "conflict"; parcels: Parcel[] };

/**
 * Baseline empty context. Carries the ambient telemetry needed by the
 * detail panel to render the correct empty-state message (chain
 * mismatch, no snapshot yet, stats refresh error). These flags are
 * already tracked internally in the viewer — passing them through
 * avoids re-implementing telemetry duplicates in the shell.
 */
export interface ViewerEmptyState {
  kind: "empty";
  chainMismatch: boolean;
  hasAnySnapshot: boolean;
  refreshError: string | null;
}

export type ViewerContextState =
  | ViewerEmptyState
  | { kind: "selection"; selection: ViewerSelectionState }
  | { kind: "parcel"; detail: ViewerParcelDetail };

/**
 * Precedence for the derivation (top wins):
 *   parcel  > conflict > cell > empty
 *
 * `conflict` and `cell` are both surfaced under `selection`. Purchase
 * is intentionally NOT part of this union — it lives in the shell.
 */
export interface DeriveContextStateInput {
  detail: ViewerParcelDetail | null;
  cellUi: ViewerCellState | null;
  conflict: Parcel[] | null;
  chainMismatch: boolean;
  hasAnySnapshot: boolean;
  refreshError: string | null;
}

export function deriveViewerContextState(
  input: DeriveContextStateInput,
): ViewerContextState {
  if (input.detail) return { kind: "parcel", detail: input.detail };
  if (input.conflict && input.conflict.length > 0) {
    return {
      kind: "selection",
      selection: { kind: "conflict", parcels: input.conflict },
    };
  }
  if (input.cellUi) {
    return {
      kind: "selection",
      selection: { kind: "cell", cell: input.cellUi },
    };
  }
  return {
    kind: "empty",
    chainMismatch: input.chainMismatch,
    hasAnySnapshot: input.hasAnySnapshot,
    refreshError: input.refreshError,
  };
}

/**
 * Structural equality for `ViewerContextState`. Used by the viewer to
 * avoid firing `onContextStateChange` when the derived state has not
 * actually changed (React effect churn guard).
 */
export function contextStatesEqual(
  a: ViewerContextState,
  b: ViewerContextState,
): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "empty" && b.kind === "empty") {
    return (
      a.chainMismatch === b.chainMismatch &&
      a.hasAnySnapshot === b.hasAnySnapshot &&
      a.refreshError === b.refreshError
    );
  }
  if (a.kind === "parcel" && b.kind === "parcel") {
    return (
      a.detail.parcel.id === b.detail.parcel.id &&
      a.detail.asOfBlock === b.detail.asOfBlock &&
      a.detail.reconciled === b.detail.reconciled &&
      a.detail.fetchError === b.detail.fetchError
    );
  }
  if (a.kind === "selection" && b.kind === "selection") {
    const sa = a.selection;
    const sb = b.selection;
    if (sa.kind !== sb.kind) return false;
    if (sa.kind === "conflict" && sb.kind === "conflict") {
      if (sa.parcels.length !== sb.parcels.length) return false;
      for (let i = 0; i < sa.parcels.length; i++) {
        if (sa.parcels[i].id !== sb.parcels[i].id) return false;
      }
      return true;
    }
    if (sa.kind === "cell" && sb.kind === "cell") {
      const ca = sa.cell;
      const cb = sb.cell;
      if (ca.kind !== cb.kind) return false;
      if (ca.x !== cb.x || ca.y !== cb.y) return false;
      if (ca.kind === "empty" && cb.kind === "empty") {
        return ca.asOf === cb.asOf;
      }
      return true;
    }
  }
  return false;
}

/**
 * Re-exported for shell composition convenience. `GridSelection` is the
 * canonical "committed rectangle" payload flowing through the existing
 * `onCommitSelection` viewer callback — the shell composes it with
 * route info to derive its own `purchase` context. See
 * `ui/grid-context.ts`.
 */
export type { GridSelection };
